import React from 'react';
import { DirectoryMember } from '../../types';
import { Avatar } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { MoreVertical, Clock, User, MessageSquare, Shield, Trash2, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import { safeFormat } from '../../lib/utils';

interface MemberListItemProps {
  member: DirectoryMember;
  isAtLeastModerator?: boolean;
  canManageRoles?: boolean;
  canManageMembers?: boolean;
  canRevoke?: boolean;
  isCurrentUser?: boolean;
  /** Organization terminology for "member" (e.g. "Soldier", "Employee"). */
  memberLabel?: string;
  onView: (member: DirectoryMember) => void;
  onMessage: (member: DirectoryMember) => void;
  onRemove: (member: DirectoryMember) => void;
  onRoleChange: (member: DirectoryMember) => void;
  activeActionMenu: string | null;
  setActiveActionMenu: (id: string | null) => void;
}

export const MemberListItem: React.FC<MemberListItemProps> = ({
  member,
  isAtLeastModerator: _isAtLeastModerator = false,
  canManageRoles = false,
  canManageMembers = false,
  canRevoke = false,
  isCurrentUser = false,
  memberLabel = 'Member',
  onView,
  onMessage,
  onRemove,
  onRoleChange,
  activeActionMenu,
  setActiveActionMenu
}) => {
  const isPending = member.metadata?.status === 'PENDING' || (member as any).status === 'PENDING';
  const displayName = member.name || member.email || (isPending ? 'Pending Invitation' : memberLabel);

  return (
    <tr 
      className="hover:bg-gray-50 transition-colors cursor-pointer group dark:bg-slate-950"
      onClick={() => onView(member)}
    >
      <td className="px-4 py-4">
        <div className="flex items-center gap-3">
          <Avatar userId={member.userId} initials={displayName[0] || '?'} size="sm" />
          <div>
            <div className="text-sm font-medium text-gray-900 group-hover:text-brand-primary transition-colors dark:text-white">{displayName}</div>
            <div className="text-xs text-gray-500">{member.metadata?.department || memberLabel}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-4">
        <Badge variant={member.role === 'ADMIN' || member.role === 'PRESIDENT' ? 'blue' : 'gray'}>
          {member.role}
        </Badge>
      </td>
      <td className="px-4 py-4">
        <Badge variant={isPending ? 'amber' : 'green'}>
          {member.metadata?.status || 'ACTIVE'}
        </Badge>
      </td>
      <td className="px-4 py-4">
        <div className="text-xs text-gray-500 flex items-center gap-1">
          <Clock size={12} />
          {safeFormat(member.joinedAt, 'MMM d, yyyy')}
        </div>
      </td>
      <td className="px-4 py-4 text-right">
        <div className="relative inline-block text-left" onClick={(e) => e.stopPropagation()}>
          <button 
            onClick={() => setActiveActionMenu(activeActionMenu === member.id ? null : member.id)}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors dark:bg-slate-800"
          >
            <MoreVertical size={18} />
          </button>

          {activeActionMenu === member.id && (
            <div className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-xl bg-white shadow-xl border border-gray-100 ring-1 ring-black ring-opacity-5 focus:outline-none animate-in fade-in zoom-in-95 duration-100 dark:bg-slate-900">
              <div className="py-1">
                <button 
                  onClick={() => {
                    onView(member);
                    setActiveActionMenu(null);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 gap-2 dark:bg-slate-950"
                >
                  <User size={16} className="text-gray-400" /> View Profile
                </button>
                {!isCurrentUser && !isPending && (
                  <button 
                    onClick={() => {
                      onMessage(member);
                      setActiveActionMenu(null);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 gap-2 dark:bg-slate-950"
                  >
                    <MessageSquare size={16} className="text-gray-400" /> Send Message
                  </button>
                )}
                {canManageRoles && !isPending && (
                  <button 
                    onClick={() => {
                      onRoleChange(member);
                      setActiveActionMenu(null);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 gap-2 dark:bg-slate-950"
                  >
                    <Shield size={16} className="text-gray-400" /> Change Role
                  </button>
                )}
                {isPending && ((member as any).inviteLink || member.metadata?.inviteLink) && (
                  <button 
                    onClick={() => {
                      const link = (member as any).inviteLink || member.metadata?.inviteLink;
                      navigator.clipboard.writeText(link);
                      toast.success('Invite link copied to clipboard!');
                      setActiveActionMenu(null);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 gap-2 dark:bg-slate-950 font-medium"
                  >
                    <Copy size={16} className="text-gray-400" /> Copy Invite Link
                  </button>
                )}
                {isPending && canRevoke && (
                  <>
                    <div className="h-px bg-gray-100 my-1 dark:bg-slate-800" />
                    <button 
                      onClick={() => {
                        onRemove(member);
                        setActiveActionMenu(null);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 gap-2 font-medium"
                    >
                      <Trash2 size={16} /> Revoke Invitation
                    </button>
                  </>
                )}
                {!isPending && canManageMembers && !isCurrentUser && (
                  <>
                    <div className="h-px bg-gray-100 my-1 dark:bg-slate-800" />
                    <button 
                      onClick={() => {
                        onRemove(member);
                        setActiveActionMenu(null);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 gap-2 font-medium"
                    >
                      <Trash2 size={16} /> Remove Member
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};
